import { createAnalyser, LEVEL_WINDOW, readLevel } from '../audio/levels'
import { routeOutput } from '../audio/output'

/** The level that counts as someone talking, for the ring around their avatar. */
export const SPEAKING_LEVEL = 0.12

export const LOCAL_LEVEL_KEY = 'local'

interface IRemote {
  element: HTMLAudioElement
  source: MediaStreamAudioSourceNode
  analyser: AnalyserNode
}

/**
 * The huddle's sound in this browser. The mic goes out through a gain (the mute) to
 * one outgoing track, which Arc's voice joins when Arc is hosted here. Each peer plays
 * through an audio element: Chrome hands a WebRTC stream to Web Audio only while
 * something plays it, and the element is also what echo cancellation listens to.
 */
export class HuddleAudio {
  private readonly micGain: GainNode

  private readonly localAnalyser: AnalyserNode

  private readonly outgoing: MediaStreamAudioDestinationNode

  /** Everything Arc should hear: this mic and every peer. */
  private readonly roomMix: GainNode

  private readonly remotes = new Map<string, IRemote>()

  private arcVoice: MediaStreamAudioSourceNode | null = null

  /** Arc's ear on the room: one connection at a time. */
  private roomTap: MediaStreamAudioDestinationNode | null = null

  private micSource: MediaStreamAudioSourceNode

  private outputId: string | null = null

  private readonly levelBuffer = new Float32Array(LEVEL_WINDOW)

  constructor(
    private mic: MediaStream,
    readonly context: AudioContext,
  ) {
    this.micSource = this.context.createMediaStreamSource(mic)
    this.micGain = this.context.createGain()
    this.localAnalyser = createAnalyser(this.context)
    this.outgoing = this.context.createMediaStreamDestination()
    this.roomMix = this.context.createGain()
    this.micSource.connect(this.micGain)
    this.micGain.connect(this.localAnalyser)
    this.micGain.connect(this.outgoing)
    this.micGain.connect(this.roomMix)
  }

  /** What every peer connection sends. */
  get outgoingTrack(): MediaStreamTrack {
    return this.outgoing.stream.getAudioTracks()[0]
  }

  /** A context that still started suspended is woken once the huddle is joined. */
  resume(): Promise<void> {
    return this.context.state === 'running' ? Promise.resolve() : this.context.resume()
  }

  /** Talk through another mic; everything downstream (peers, Arc, the mute) carries on. */
  replaceMic(mic: MediaStream): void {
    this.micSource.disconnect()
    this.mic.getTracks().forEach((track) => track.stop())
    this.mic = mic
    this.micSource = this.context.createMediaStreamSource(mic)
    this.micSource.connect(this.micGain)
  }

  /** Play the room through another output; null is the system default. */
  setOutputDevice(outputId: string | null): void {
    this.outputId = outputId
    this.remotes.forEach(({ element }) => routeOutput(element, outputId))
  }

  setMuted(isMuted: boolean): void {
    this.micGain.gain.value = isMuted ? 0 : 1
  }

  addRemote(peerId: string, stream: MediaStream): void {
    this.removeRemote(peerId)
    const element = new Audio()
    element.autoplay = true
    element.srcObject = stream
    if (this.outputId) routeOutput(element, this.outputId)
    void element.play().catch(() => {})
    const source = this.context.createMediaStreamSource(stream)
    const analyser = createAnalyser(this.context)
    source.connect(analyser)
    source.connect(this.roomMix)
    this.remotes.set(peerId, { element, source, analyser })
  }

  removeRemote(peerId: string): void {
    const remote = this.remotes.get(peerId)
    if (!remote) return
    remote.element.pause()
    remote.element.srcObject = null
    remote.source.disconnect()
    this.remotes.delete(peerId)
  }

  /** 0–1 per peer id, and this browser's own mic under LOCAL_LEVEL_KEY. */
  levels(): Map<string, number> {
    const levels = new Map([[LOCAL_LEVEL_KEY, readLevel(this.localAnalyser, this.levelBuffer)]])
    this.remotes.forEach(({ analyser }, peerId) => {
      levels.set(peerId, readLevel(analyser, this.levelBuffer))
    })
    return levels
  }

  /** This browser's own voice after the mute, for the transcript. */
  openOwnVoice(): MediaStream {
    const destination = this.context.createMediaStreamDestination()
    this.micGain.connect(destination)
    return destination.stream
  }

  /** The room as Arc hears it. Fresh each time: Arc's connection stops its input on close. */
  openRoomAudio(): MediaStream {
    this.closeRoomAudio()
    this.roomTap = this.context.createMediaStreamDestination()
    this.roomMix.connect(this.roomTap)
    return this.roomTap.stream
  }

  closeRoomAudio(): void {
    if (this.roomTap) this.roomMix.disconnect(this.roomTap)
    this.roomTap = null
  }

  /** Arc's voice into the outgoing track, so the others hear it. It already plays here. */
  setArcVoice(stream: MediaStream | null): void {
    this.arcVoice?.disconnect()
    this.arcVoice = null
    if (!stream) return
    this.arcVoice = this.context.createMediaStreamSource(stream)
    this.arcVoice.connect(this.outgoing)
  }

  close(): void {
    ;[...this.remotes.keys()].forEach((peerId) => this.removeRemote(peerId))
    this.setArcVoice(null)
    this.closeRoomAudio()
    this.mic.getTracks().forEach((track) => track.stop())
    void this.context.close()
  }
}
